import React, { useState, useCallback } from 'react';
import adminApi from '../lib/adminApi';
import { toast } from 'sonner';
import {
  ShieldCheck, ShieldAlert, AlertTriangle, CheckCircle2, XCircle,
  RefreshCw, Wrench, Play, Eye, ChevronDown, ChevronRight,
  Activity, FileText, Calculator, CalendarCheck, Link2, Database,
  TrendingUp, Users, Archive, CreditCard, Clock, BarChart3, Loader2,
  MessageSquare
} from 'lucide-react';
import { formatNumber } from '../lib/formatters';

const CATEGORY_META = {
  invoice_math: { label: 'Invoice Math', icon: Calculator, color: 'text-blue-600' },
  gst_verification: { label: 'GST Verification', icon: FileText, color: 'text-amber-600' },
  date_integrity: { label: 'Date Integrity', icon: CalendarCheck, color: 'text-teal-600' },
  formula_cross_check: { label: 'Formula Cross-Check', icon: Activity, color: 'text-indigo-600' },
  kpi_verification: { label: 'KPI Verification', icon: BarChart3, color: 'text-purple-600' },
  status_treatment: { label: 'Status Treatment', icon: Clock, color: 'text-orange-600' },
  aged_receivables: { label: 'Aged Receivables', icon: TrendingUp, color: 'text-rose-600' },
  data_integrity: { label: 'Data Integrity', icon: Database, color: 'text-red-600' },
  exclusion_verification: { label: 'Exclusion Verification', icon: Archive, color: 'text-neutral-600' },
  payment_records: { label: 'Payment Records', icon: CreditCard, color: 'text-green-600' },
  revenue_trend: { label: 'Revenue Trend', icon: TrendingUp, color: 'text-emerald-600' },
  client_breakdown: { label: 'Client Breakdown', icon: Users, color: 'text-sky-600' },
  inventory: { label: 'Inventory', icon: Link2, color: 'text-violet-600' },
};

const StatusBadge = ({ status }) => {
  const config = {
    PASS: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', icon: CheckCircle2 },
    FAIL: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', icon: XCircle },
    WARN: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', icon: AlertTriangle },
  }[status] || { bg: 'bg-white/40 border-white/40', text: 'text-neutral-600', icon: Activity };
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
};

const HealthGauge = ({ score }) => {
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 90 ? '#10b981' : score >= 70 ? '#f59e0b' : '#ef4444';
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width="180" height="180" viewBox="0 0 180 180">
        <circle cx="90" cy="90" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="12" />
        <circle cx="90" cy="90" r={radius} fill="none" stroke={color} strokeWidth="12"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 90 90)"
          style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="text-4xl font-bold" style={{ color }}>{score}%</span>
        <span className="text-xs text-neutral-500 mt-0.5">Health Score</span>
      </div>
    </div>
  );
};

const fmt = (n) => {
  if (n == null) return '—';
  return formatNumber(n, 2);
};

export default function DataHealthPage() {
  const [audit, setAudit] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [consistency, setConsistency] = useState(null);
  const [repairPreviews, setRepairPreviews] = useState(null);
  const [repairResults, setRepairResults] = useState(null);
  const [brokenImages, setBrokenImages] = useState(null);
  const [loading, setLoading] = useState({ audit: false, comparison: false, consistency: false, preview: false, apply: false, images: false });
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [confirmApply, setConfirmApply] = useState(false);

  const setLoadingKey = (key, val) => setLoading(prev => ({ ...prev, [key]: val }));

  const runAudit = useCallback(async () => {
    setLoadingKey('audit', true);
    setLoadingKey('images', true);
    try {
      const [auditRes, imagesRes] = await Promise.all([
        adminApi.get('/api/admin/accounting/audit'),
        adminApi.get('/api/admin/accounting/audit/broken-images'),
      ]);
      setAudit(auditRes.data);
      setBrokenImages(imagesRes.data);
      toast.success(`Audit complete — Health Score: ${auditRes.data.health_score}%`);
    } catch (e) {
      toast.error('Audit failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingKey('audit', false);
      setLoadingKey('images', false);
    }
  }, []);

  const runComparison = useCallback(async () => {
    setLoadingKey('comparison', true);
    try {
      const { data } = await adminApi.get('/api/admin/accounting/audit/dashboard-comparison');
      setComparison(data);
      toast.success(data.all_metrics_match ? 'All KPIs match raw data!' : 'KPI discrepancies found');
    } catch (e) {
      toast.error('Comparison failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingKey('comparison', false);
    }
  }, []);

  // Consistency Check — hits key aggregations 3× each + cross-checks aggregation
  // results against raw scans. Any FAIL means an endpoint is non-idempotent.
  const runConsistency = useCallback(async () => {
    setLoadingKey('consistency', true);
    try {
      const { data } = await adminApi.get('/api/admin/accounting/data-health/consistency');
      setConsistency(data);
      if (data.failing > 0 || data.warning > 0) {
        toast.error(`${data.failing} inconsistent · ${data.warning} mismatched`);
      } else {
        toast.success(`All ${data.total} checks passed`);
      }
    } catch (e) {
      toast.error('Consistency check failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingKey('consistency', false);
    }
  }, []);

  const previewRepairs = useCallback(async () => {
    setLoadingKey('preview', true);
    try {
      const { data } = await adminApi.post('/api/admin/accounting/audit/repair/run-all?dry_run=true');
      setRepairPreviews(data);
      setRepairResults(null);
      toast.success(`Preview: ${data.total_fixes} fix(es) found`);
    } catch (e) {
      toast.error('Preview failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingKey('preview', false);
    }
  }, []);

  const applyRepairs = useCallback(async () => {
    setLoadingKey('apply', true);
    setConfirmApply(false);
    try {
      const { data } = await adminApi.post('/api/admin/accounting/audit/repair/run-all?dry_run=false');
      setRepairResults(data);
      setRepairPreviews(null);
      toast.success(`Applied ${data.total_fixes} fix(es) successfully!`);
      // Re-run audit to show updated score
      const auditRes = await adminApi.get('/api/admin/accounting/audit');
      setAudit(auditRes.data);
    } catch (e) {
      toast.error('Repair failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingKey('apply', false);
    }
  }, []);

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  // Group checks by category
  const groupedChecks = audit?.checks?.reduce((acc, check) => {
    const cat = check.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(check);
    return acc;
  }, {}) || {};

  const isAnyLoading = Object.values(loading).some(Boolean);

  return (
    <div className="space-y-6" data-testid="data-health-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-lg font-bold text-[#1a1a2e]" data-testid="data-health-title">Data Health</h1>
          <p className="text-xs text-neutral-500 mt-0.5">Audit accounting data integrity and repair discrepancies</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={runAudit} disabled={isAnyLoading}
            data-testid="run-audit-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-colors">
            {loading.audit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
            Run Audit
          </button>
          <button onClick={runComparison} disabled={isAnyLoading}
            data-testid="run-comparison-btn"
            className="inline-flex items-center gap-2 px-4 py-2 glass-card text-neutral-700 text-sm font-medium rounded-xl hover:bg-white/60 disabled:opacity-50 transition-colors">
            {loading.comparison ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
            Compare KPIs
          </button>
          <button onClick={runConsistency} disabled={isAnyLoading}
            data-testid="run-consistency-btn"
            className="inline-flex items-center gap-2 px-4 py-2 glass-card text-neutral-700 text-sm font-medium rounded-xl hover:bg-white/60 disabled:opacity-50 transition-colors">
            {loading.consistency ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Consistency Check
          </button>
        </div>
      </div>

      {/* Consistency Check panel — runs key aggregations 3× each + cross-checks
          them against raw scans. Fresh regression detector for endpoints that
          might silently return different numbers over time. */}
      {consistency && (
        <div className="soft-card overflow-hidden" data-testid="consistency-results">
          <div className="px-5 py-3 border-b border-white/30 bg-white/40 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-sm font-semibold text-[#1a1a2e]">Endpoint Consistency</h2>
              <p className="text-xs text-neutral-500">Each check runs 3× to detect non-idempotent aggregations; cross-checks compare aggregations against raw scans.</p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{consistency.passing} pass</span>
              {consistency.failing > 0 && <span className="px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">{consistency.failing} fail</span>}
              {consistency.warning > 0 && <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{consistency.warning} mismatch</span>}
              {consistency.errors > 0 && <span className="px-2 py-1 rounded-full bg-neutral-50 text-neutral-700 border border-neutral-200">{consistency.errors} error</span>}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/40 text-left">
                  <th className="px-4 py-2 font-medium text-neutral-600 w-24">Status</th>
                  <th className="px-4 py-2 font-medium text-neutral-600">Check</th>
                  <th className="px-4 py-2 font-medium text-neutral-600 text-right w-64">Runs / Values</th>
                  <th className="px-4 py-2 font-medium text-neutral-600">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/30">
                {consistency.checks.map((c, i) => (
                  <tr key={i} className={c.status === 'PASS' ? 'hover:bg-white/60' : 'bg-red-50/30 hover:bg-red-50/50'} data-testid={`consistency-row-${i}`}>
                    <td className="px-4 py-2"><StatusBadge status={c.status} /></td>
                    <td className="px-4 py-2 text-[#1a1a2e]">{c.name}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-xs text-neutral-600">
                      {(c.runs || []).map((v, k) => (
                        <span key={k} className="ml-2 inline-block px-1.5 py-0.5 rounded bg-white/60 border border-white/40">{typeof v === 'number' ? v.toLocaleString('en-IN') : String(v)}</span>
                      ))}
                    </td>
                    <td className="px-4 py-2 text-xs text-neutral-500">{c.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-2 text-[11px] text-neutral-500 bg-white/40 border-t border-white/30">
            Generated {consistency.generated_at?.split('T').join(' ').slice(0, 19)} UTC · {consistency.total} checks
          </div>
        </div>
      )}

      {/* Phone number normalization panel — always-visible self-serve tool
          for auditing every phone field across employees / career users /
          whatsapp subscribers, and one-click applying the `+91XXXXXXXXXX`
          canonical form back. Flagged records surface below with the reason. */}
      <PhoneHealthPanel />

      {/* WhatsApp master control — one toggle per automated flow. Super-admin
          only. Prod-safe emergency kill switches. */}
      <WhatsAppControlPanel />

      {/* Email master control + preview — mirrors WhatsApp, but for the
          well-branded email versions of the same 5 flows. */}
      <EmailControlPanel />

      {/* On-demand task digest — send pending/overdue or today/tomorrow to any
          employee, right now, reusing the existing approved templates. */}
      <AdHocDigestPanel />

      {/* Schedule editor — change the 5 cron times (IST) live, no deploy. */}
      <ScheduleEditorPanel />

      {/* Send log dashboard — every WhatsApp with filters + retry + delivery. */}
      <SendLogPanel />

      {/* Empty State */}
      {!audit && !comparison && (
        <div className="glass-card p-12 text-center" data-testid="empty-state">
          <ShieldCheck className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-neutral-700">No audit data yet</h3>
          <p className="text-sm text-neutral-500 mt-1 mb-4">Click &quot;Run Audit&quot; to scan your accounting data for integrity issues.</p>
        </div>
      )}

      {/* Audit Results */}
      {audit && (
        <>
          {/* Score + Summary Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <div className="glass-card p-6 flex flex-col items-center justify-center lg:row-span-2" data-testid="health-score-card">
              <HealthGauge score={audit.health_score} />
              <p className="text-xs text-neutral-500 mt-3">{audit.timestamp?.split('T')[0]}</p>
            </div>
            <SummaryCard label="Total Checks" value={audit.summary.total_checks} icon={Activity} color="bg-blue-50 text-blue-700" />
            <SummaryCard label="Passed" value={audit.summary.passed} icon={CheckCircle2} color="bg-emerald-50 text-emerald-700" />
            <SummaryCard label="Failed" value={audit.summary.failed} icon={XCircle} color="bg-red-50 text-red-700" />
            <SummaryCard label="Warnings" value={audit.summary.warnings} icon={AlertTriangle} color="bg-amber-50 text-amber-700" />
            <SummaryCard label="Total Invoices" value={audit.snapshot.total_invoices} icon={FileText} color="bg-white/40 text-neutral-700" />
            <SummaryCard label="Active Invoices" value={audit.snapshot.active} icon={Database} color="bg-indigo-50 text-indigo-700" />
          </div>

          {/* KPIs */}
          <div className="soft-card overflow-hidden" data-testid="kpi-section">
            <div className="px-5 py-3 border-b border-white/30 bg-white/40">
              <h2 className="text-sm font-semibold text-[#1a1a2e]">Expected KPIs (from raw data)</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y divide-white/30">
              {Object.entries(audit.expected_kpis).map(([k, v]) => (
                <div key={k} className="p-4">
                  <p className="text-xs text-neutral-500 capitalize">{k.replace(/_/g, ' ')}</p>
                  <p className="text-base font-semibold text-[#1a1a2e] mt-0.5">
                    {k === 'collection_rate' ? `${v}%` : `₹${fmt(v)}`}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Status Financial Summary */}
          {audit.status_financial_summary && (
            <div className="soft-card overflow-hidden" data-testid="status-financial-summary">
              <div className="px-5 py-3 border-b border-white/30 bg-white/40">
                <h2 className="text-sm font-semibold text-[#1a1a2e]">Status-wise Financial Summary</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-white/40 text-left">
                      <th className="px-4 py-2 font-medium text-neutral-600">Status</th>
                      <th className="px-4 py-2 font-medium text-neutral-600 text-right">Count</th>
                      <th className="px-4 py-2 font-medium text-neutral-600 text-right">Subtotal</th>
                      <th className="px-4 py-2 font-medium text-neutral-600 text-right">GST</th>
                      <th className="px-4 py-2 font-medium text-neutral-600 text-right">TDS</th>
                      <th className="px-4 py-2 font-medium text-neutral-600 text-right">Total</th>
                      <th className="px-4 py-2 font-medium text-neutral-600 text-right">Received</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/30">
                    {Object.entries(audit.status_financial_summary).map(([status, d]) => (
                      <tr key={status} className="hover:bg-white/60">
                        <td className="px-4 py-2 capitalize font-medium text-[#1a1a2e]">{status}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{d.count}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{fmt(d.subtotal)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{fmt(d.gst)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{fmt(d.tds)}</td>
                        <td className="px-4 py-2 text-right tabular-nums font-medium">{fmt(d.total)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{fmt(d.received)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Check Results by Category */}
          <div className="soft-card overflow-hidden" data-testid="checks-section">
            <div className="px-5 py-3 border-b border-white/30 bg-white/40 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-[#1a1a2e]">Detailed Check Results</h2>
              <span className="text-xs text-neutral-500">
                {Object.keys(groupedChecks).length} categories
              </span>
            </div>
            <div className="divide-y divide-white/30">
              {Object.entries(groupedChecks).map(([cat, checks]) => {
                const meta = CATEGORY_META[cat] || { label: cat, icon: Activity, color: 'text-neutral-600' };
                const CatIcon = meta.icon;
                const hasIssues = checks.some(c => c.status !== 'PASS');
                const isExpanded = expandedCategories.includes(cat);
                return (
                  <div key={cat}>
                    <button onClick={() => toggleCategory(cat)}
                      className="w-full flex items-center gap-3 px-5 py-3 hover:bg-white/60 transition-colors text-left">
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-neutral-400" /> : <ChevronRight className="w-4 h-4 text-neutral-400" />}
                      <CatIcon className={`w-4 h-4 ${meta.color}`} />
                      <span className="text-sm font-medium text-[#1a1a2e] flex-1">{meta.label}</span>
                      <div className="flex gap-1.5">
                        {checks.map((c, i) => (
                          <StatusBadge key={i} status={c.status} />
                        ))}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="bg-white/40 border-t border-white/30 px-5 py-3 space-y-2">
                        {checks.map((c, i) => (
                          <div key={i} className="flex items-start gap-3 py-2">
                            <StatusBadge status={c.status} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-neutral-700">{c.check}</p>
                              {c.detail && (
                                <pre className="text-xs text-neutral-500 mt-1 whitespace-pre-wrap bg-white rounded p-2 border border-white/30 max-h-40 overflow-auto">
                                  {JSON.stringify(c.detail, null, 2)}
                                </pre>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Broken Image References */}
          {brokenImages && brokenImages.broken_count > 0 && (
            <div className="bg-white rounded-xl border border-red-200 overflow-hidden" data-testid="broken-images-section">
              <div className="px-5 py-3 border-b border-red-100 bg-red-50/50 flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-600" />
                <h2 className="text-sm font-semibold text-red-800">
                  {brokenImages.broken_count} Broken Image Reference{brokenImages.broken_count !== 1 ? 's' : ''}
                </h2>
                <span className="ml-auto text-xs text-red-600">
                  These images were lost during deployment — re-upload to fix
                </span>
              </div>
              <div className="divide-y divide-red-100">
                {brokenImages.broken_references.map((b, i) => (
                  <div key={i} className="px-5 py-2.5 flex items-center gap-3 text-sm">
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">{b.collection}</span>
                    <span className="text-[#1a1a2e] font-medium">{b.identifier}</span>
                    <span className="text-neutral-400 text-xs truncate flex-1">{b.url}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {brokenImages && brokenImages.broken_count === 0 && (
            <div className="bg-emerald-50/50 border border-emerald-200 rounded-xl px-5 py-3 flex items-center gap-2" data-testid="images-healthy">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-emerald-700">All {brokenImages.total_local_refs} image references are healthy</span>
            </div>
          )}
        </>
      )}

      {/* KPI Comparison */}
      {comparison && (
        <div className="soft-card overflow-hidden" data-testid="comparison-section">
          <div className="px-5 py-3 border-b border-white/30 bg-white/40 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-neutral-600" />
            <h2 className="text-sm font-semibold text-[#1a1a2e]">Dashboard vs Audit KPI Comparison</h2>
            {comparison.all_metrics_match ? (
              <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-medium text-emerald-700">
                <CheckCircle2 className="w-3 h-3" /> All Match
              </span>
            ) : (
              <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 border border-red-200 rounded-full text-xs font-medium text-red-700">
                <XCircle className="w-3 h-3" /> Discrepancies Found
              </span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/40 text-left">
                  <th className="px-4 py-2 font-medium text-neutral-600">Metric</th>
                  <th className="px-4 py-2 font-medium text-neutral-600 text-right">Dashboard</th>
                  <th className="px-4 py-2 font-medium text-neutral-600 text-right">Audit (Raw Data)</th>
                  <th className="px-4 py-2 font-medium text-neutral-600 text-right">Difference</th>
                  <th className="px-4 py-2 font-medium text-neutral-600 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/30">
                {comparison.comparisons.map((c) => (
                  <tr key={c.metric} className={!c.match ? 'bg-red-50/30' : 'hover:bg-white/60'}>
                    <td className="px-4 py-2.5 capitalize font-medium text-[#1a1a2e]">{c.metric.replace(/_/g, ' ')}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">₹{fmt(c.dashboard_value)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">₹{fmt(c.audit_value)}</td>
                    <td className={`px-4 py-2.5 text-right tabular-nums font-medium ${c.difference > 0 ? 'text-red-600' : 'text-neutral-500'}`}>
                      {c.difference > 0 ? `₹${fmt(c.difference)}` : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {c.match ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" /> : <XCircle className="w-4 h-4 text-red-500 mx-auto" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Repair Section */}
      <div className="soft-card overflow-hidden" data-testid="repair-section">
        <div className="px-5 py-3 border-b border-white/30 bg-white/40 flex items-center gap-2">
          <Wrench className="w-4 h-4 text-neutral-600" />
          <h2 className="text-sm font-semibold text-[#1a1a2e]">Data Repairs</h2>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-neutral-600">
            Run a dry-run preview first, then apply fixes. Each repair stores a backup and is safe to run multiple times.
          </p>
          <div className="flex gap-2 flex-wrap">
            <button onClick={previewRepairs} disabled={isAnyLoading}
              data-testid="preview-repairs-btn"
              className="inline-flex items-center gap-2 px-4 py-2 glass-card text-neutral-700 text-sm font-medium rounded-xl hover:bg-white/60 disabled:opacity-50 transition-colors">
              {loading.preview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              Preview All Repairs
            </button>
            <button onClick={() => setConfirmApply(true)} disabled={isAnyLoading || (!repairPreviews && !audit)}
              data-testid="apply-repairs-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors">
              {loading.apply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Apply All Repairs
            </button>
          </div>

          {/* Preview Results */}
          {repairPreviews && (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/50 p-4 space-y-3" data-testid="repair-preview-results">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-amber-600" />
                <h3 className="text-sm font-semibold text-amber-800">Preview: {repairPreviews.total_fixes} fix(es) found</h3>
              </div>
              {Object.entries(repairPreviews.results).map(([key, r]) => (
                <RepairResultCard key={key} name={key} result={r} isPreview />
              ))}
            </div>
          )}

          {/* Applied Results */}
          {repairResults && (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 space-y-3" data-testid="repair-applied-results">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <h3 className="text-sm font-semibold text-emerald-800">Applied: {repairResults.total_fixes} fix(es) completed</h3>
              </div>
              {Object.entries(repairResults.results).map(([key, r]) => (
                <RepairResultCard key={key} name={key} result={r} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Confirm Dialog */}
      {confirmApply && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="confirm-apply-dialog">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <ShieldAlert className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-[#1a1a2e]">Confirm Data Repair</h3>
                <p className="text-xs text-neutral-500">This will modify invoice records</p>
              </div>
            </div>
            <p className="text-sm text-neutral-600 mb-5">
              This will apply all repairs (invoice math, GST sync, date normalization, status corrections, duplicate renaming) to your live data.
              Each change stores a backup and is reversible. Are you sure?
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmApply(false)}
                className="px-4 py-2 text-sm text-neutral-600 hover:text-[#1a1a2e] font-medium">Cancel</button>
              <button onClick={applyRepairs} data-testid="confirm-apply-btn"
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-xl hover:bg-red-700">
                Apply Repairs
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color }) {
  return (
    <div className="glass-card p-4 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-xs text-neutral-500">{label}</p>
        <p className="text-xl font-bold text-[#1a1a2e] tabular-nums">{value}</p>
      </div>
    </div>
  );
}

function RepairResultCard({ name, result, isPreview }) {
  const [expanded, setExpanded] = useState(false);
  const label = name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  if (result.fixes_count === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-neutral-500">
        <CheckCircle2 className="w-3.5 h-3.5 text-neutral-400" />
        <span className="font-medium">{label}:</span> No issues found
      </div>
    );
  }
  return (
    <div>
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 text-sm w-full text-left">
        {expanded ? <ChevronDown className="w-3.5 h-3.5 text-neutral-400" /> : <ChevronRight className="w-3.5 h-3.5 text-neutral-400" />}
        <Wrench className={`w-3.5 h-3.5 ${isPreview ? 'text-amber-500' : 'text-emerald-500'}`} />
        <span className="font-medium text-[#1a1a2e]">{label}:</span>
        <span className={isPreview ? 'text-amber-700' : 'text-emerald-700'}>{result.fixes_count} fix(es)</span>
      </button>
      {expanded && result.fixes && (
        <pre className="text-xs text-neutral-600 mt-2 ml-8 bg-white rounded p-2 border border-white/30 max-h-48 overflow-auto whitespace-pre-wrap">
          {JSON.stringify(Array.isArray(result.fixes) ? result.fixes.slice(0, 20) : result.fixes, null, 2)}
        </pre>
      )}
    </div>
  );
}

// ─── Phone Health Panel ────────────────────────────────────────────────────
// Self-serve UI on top of `/api/admin/phones/audit` + `/api/admin/phones/normalize`.
// Auditors can eye-verify every phone across the org and one-click apply +91.
function PhoneHealthPanel() {
  const [report, setReport] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [applying, setApplying] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  const runAudit = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get('/api/admin/phones/audit');
      setReport(r.data);
      setExpanded(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Audit failed');
    } finally { setLoading(false); }
  };

  const applyNormalize = async () => {
    if (!window.confirm('Apply +91 canonical form to every normalizable phone across employees, career users and WhatsApp subscribers?')) return;
    setApplying(true);
    try {
      const r = await adminApi.post('/api/admin/phones/normalize?confirm=true');
      setReport(r.data);
      setExpanded(true);
      toast.success(`Normalized ${r.data.normalized} phone(s) · ${r.data.flagged_count} still need manual review`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Normalize failed');
    } finally { setApplying(false); }
  };

  React.useEffect(() => { runAudit(); }, []);

  return (
    <div className="soft-card overflow-hidden" data-testid="phone-health-panel">
      <div className="px-5 py-3 border-b border-white/30 bg-white/40 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold text-[#1a1a2e]">Phone Number Health</h2>
          <p className="text-xs text-neutral-500">
            Auto-adds <code className="bg-white/60 px-1 rounded">+91</code> where missing across employees, career users
            and WhatsApp subscribers. Flags leading-zero / too-long / invalid mobile-start records for manual review.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={runAudit} disabled={loading || applying}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-white/60 border border-white/40 hover:bg-white/80 disabled:opacity-50"
            data-testid="phone-audit-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
            Re-audit
          </button>
          <button onClick={applyNormalize} disabled={loading || applying || !report}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            data-testid="phone-normalize-btn">
            {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5" />}
            Apply +91 to Clean Records
          </button>
        </div>
      </div>

      {report && (
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <PhoneStat label="Total Checked" value={report.total_checked} color="bg-blue-50 text-blue-700" />
            <PhoneStat label="Already +91-canonical" value={report.already_canonical} color="bg-emerald-50 text-emerald-700" />
            <PhoneStat label={report.applied ? 'Normalized (written)' : 'Ready to normalize'} value={report.normalized} color="bg-indigo-50 text-indigo-700" />
            <PhoneStat label="Flagged (manual)" value={report.flagged_count} color="bg-amber-50 text-amber-700" />
          </div>
          <p className="text-xs text-neutral-500">
            <strong>{report.empty_or_missing}</strong> field(s) were empty and skipped. Foreign numbers in strict E.164
            (e.g. <code className="bg-white/60 px-1 rounded">+1234567890</code>) are counted as canonical and left untouched.
          </p>

          {report.flagged_count > 0 && (
            <div>
              <button onClick={() => setExpanded(!expanded)}
                className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                data-testid="phone-flagged-toggle">
                {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                {expanded ? 'Hide' : 'Show'} {report.flagged_count} flagged record{report.flagged_count === 1 ? '' : 's'}
              </button>
              {expanded && (
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-xs" data-testid="phone-flagged-table">
                    <thead>
                      <tr className="bg-white/40 text-left">
                        <th className="px-3 py-1.5 font-medium text-neutral-600">Where</th>
                        <th className="px-3 py-1.5 font-medium text-neutral-600">Name / Label</th>
                        <th className="px-3 py-1.5 font-medium text-neutral-600">Field</th>
                        <th className="px-3 py-1.5 font-medium text-neutral-600">Current Value</th>
                        <th className="px-3 py-1.5 font-medium text-neutral-600">Why</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/30">
                      {report.flagged.map((f, i) => (
                        <tr key={i} className="bg-amber-50/40 hover:bg-amber-50/70">
                          <td className="px-3 py-1.5 text-neutral-700 whitespace-nowrap">{f.collection}</td>
                          <td className="px-3 py-1.5 text-neutral-800 max-w-[200px] truncate" title={f.name}>{f.name}</td>
                          <td className="px-3 py-1.5 text-neutral-500 whitespace-nowrap font-mono">{f.field}</td>
                          <td className="px-3 py-1.5 text-red-700 font-mono">{String(f.raw)}</td>
                          <td className="px-3 py-1.5 text-amber-700">{f.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PhoneStat({ label, value, color }) {
  return (
    <div className="rounded-lg border border-white/40 p-2.5 bg-white/60">
      <p className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${color}`}>{label}</p>
      <p className="text-xl font-semibold text-[#1a1a2e] tabular-nums mt-1">{value ?? '—'}</p>
    </div>
  );
}

// ─── WhatsApp Master Control Panel ────────────────────────────────────────
// One switch per automated flow. Backed by GET/PUT /api/admin/whatsapp/toggles.
// Turning a flow off writes a bool to `whatsapp_flow_toggles` and every send
// path checks it before firing — no restart needed.
const WA_FLOWS = [
  { key: 'invoice_sends',    label: 'Invoice sends',            desc: 'Admin clicking "Send on WhatsApp" on any invoice' },
  { key: 'admin_digest',     label: 'Admin daily digest (9 AM)', desc: 'Numbers snapshot to every whatsapp_subscriber' },
  { key: 'client_reminders', label: 'Client reminders (10 AM / 6 PM)', desc: 'Internal nudge to each client\'s account-manager POC — never to the client' },
  { key: 'employee_sod',     label: 'Employee SOD (10 AM)',     desc: 'Today\'s + tomorrow\'s tasks per employee' },
  { key: 'employee_eod',     label: 'Employee EOD (8 PM)',      desc: 'Completed today + overdue reminders per employee' },
];

function WhatsAppControlPanel() {
  const [toggles, setToggles] = React.useState(null);
  const [saving, setSaving] = React.useState(null);
  const [error, setError] = React.useState(null);

  const load = async () => {
    try {
      const r = await adminApi.get('/api/admin/whatsapp/toggles');
      setToggles(r.data.toggles);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load toggles');
    }
  };
  React.useEffect(() => { load(); }, []);

  const setFlow = async (key, value) => {
    setSaving(key);
    try {
      const r = await adminApi.put('/api/admin/whatsapp/toggles', { [key]: value });
      setToggles(r.data.toggles);
      toast.success(`${key.replace(/_/g, ' ')} ${value ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save toggle');
    } finally { setSaving(null); }
  };

  const allOn  = toggles && WA_FLOWS.every(f => toggles[f.key]);
  const allOff = toggles && WA_FLOWS.every(f => !toggles[f.key]);
  const bulkSet = async (value) => {
    if (!window.confirm(`${value ? 'Turn ON' : 'Turn OFF'} EVERY automated WhatsApp flow?`)) return;
    setSaving('__all__');
    try {
      const patch = Object.fromEntries(WA_FLOWS.map(f => [f.key, value]));
      const r = await adminApi.put('/api/admin/whatsapp/toggles', patch);
      setToggles(r.data.toggles);
      toast.success(`All flows ${value ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk toggle failed');
    } finally { setSaving(null); }
  };

  return (
    <div className="soft-card overflow-hidden" data-testid="whatsapp-control-panel">
      <div className="px-5 py-3 border-b border-white/30 bg-white/40 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold text-[#1a1a2e]">WhatsApp Master Control</h2>
          <p className="text-xs text-neutral-500">
            Emergency kill-switches for every automated WhatsApp flow.
            Changes take effect immediately — no deploy needed.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => bulkSet(false)} disabled={!toggles || allOff || saving}
            className="px-3 py-1.5 text-xs rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
            data-testid="wa-panic-off">
            🛑 Turn all OFF
          </button>
          <button onClick={() => bulkSet(true)} disabled={!toggles || allOn || saving}
            className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
            data-testid="wa-panic-on">
            Turn all ON
          </button>
        </div>
      </div>
      {error && <div className="p-4 text-sm text-red-700">{error}</div>}
      {toggles && (
        <div className="divide-y divide-white/30">
          {WA_FLOWS.map(f => (
            <div key={f.key} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-white/40" data-testid={`wa-toggle-row-${f.key}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#1a1a2e]">{f.label}</p>
                <p className="text-xs text-neutral-500">{f.desc}</p>
              </div>
              <button
                onClick={() => setFlow(f.key, !toggles[f.key])}
                disabled={saving === f.key || saving === '__all__'}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  toggles[f.key] ? 'bg-emerald-500' : 'bg-neutral-300'
                } ${saving === f.key ? 'opacity-50' : ''}`}
                data-testid={`wa-toggle-${f.key}`}
                aria-checked={!!toggles[f.key]}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                  toggles[f.key] ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          ))}
        </div>
      )}
      {!toggles && !error && <div className="p-4 text-sm text-neutral-500">Loading…</div>}
    </div>
  );
}

// ─── Ad-hoc Task Digest Panel ─────────────────────────────────────────────
// Admin picks an employee + a mode (now = pending+overdue via EOD template,
// plan = today+tomorrow via SOD template) and fires a WhatsApp immediately.
// No new templates — reuses `hed_employee_end_of_day` / `hed_employee_start_of_day`.
function AdHocDigestPanel() {
  const [employees, setEmployees] = React.useState([]);
  const [employeeId, setEmployeeId] = React.useState('');
  const [mode, setMode] = React.useState('now');
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    adminApi.get('/api/admin/employees').then(r => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.employees || []);
      setEmployees(list);
    }).catch(() => {});
  }, []);

  const fire = async () => {
    if (!employeeId) { toast.error('Pick an employee first'); return; }
    setSending(true); setResult(null); setError(null);
    try {
      const r = await adminApi.post('/api/admin/whatsapp/ad-hoc/employee-digest', {
        employee_id: employeeId, mode,
      });
      setResult(r.data);
      toast.success(`Sent ${r.data.sent} message(s) to ${r.data.employee_name}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Send failed';
      setError(detail); toast.error(detail);
    } finally { setSending(false); }
  };

  return (
    <div className="soft-card overflow-hidden" data-testid="wa-adhoc-panel">
      <div className="px-5 py-3 border-b border-white/30 bg-white/40">
        <h2 className="text-sm font-semibold text-[#1a1a2e]">Send Task Digest — Right Now</h2>
        <p className="text-xs text-neutral-500">
          Fire a one-off WhatsApp to any employee with their pending / overdue /
          today / tomorrow tasks. Reuses the existing approved templates.
        </p>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Employee</label>
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            className="w-full px-2 py-1.5 border border-white/50 rounded-lg text-sm bg-white/70"
            data-testid="wa-adhoc-employee">
            <option value="">— pick —</option>
            {employees.map(e => (
              <option key={e._id} value={e._id}>
                {e.personal?.full_name || e.work_email || e._id}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Mode</label>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            className="w-full px-2 py-1.5 border border-white/50 rounded-lg text-sm bg-white/70"
            data-testid="wa-adhoc-mode">
            <option value="now">Now — Pending + Overdue (EOD template)</option>
            <option value="plan">Plan — Today + Tomorrow (SOD template)</option>
          </select>
        </div>
        <div>
          <button
            onClick={fire}
            disabled={sending || !employeeId}
            className="w-full px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-40 inline-flex items-center gap-1.5 justify-center"
            data-testid="wa-adhoc-send">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
            Send WhatsApp
          </button>
        </div>
      </div>
      {result && (
        <div className="mx-4 mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs" data-testid="wa-adhoc-result">
          <p className="text-emerald-800">
            ✅ Sent <strong>{result.sent}</strong> message part(s) to
            <strong> {result.employee_name}</strong> at <code>{result.phone}</code>
          </p>
          <p className="text-neutral-600 mt-1">
            Template: <code>{result.template}</code> · Var1 tasks: {result.var1_count} · Var2 tasks: {result.var2_count}
            {result.failed > 0 && <> · <span className="text-red-600">Failed: {result.failed}</span></>}
          </p>
        </div>
      )}
      {error && (
        <div className="mx-4 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700" data-testid="wa-adhoc-error">
          {error}
        </div>
      )}
    </div>
  );
}

// ─── Schedule Editor Panel ────────────────────────────────────────────────
const _SCHEDULE_FIELDS = [
  { key: 'admin_digest',   label: 'Admin Daily Digest' },
  { key: 'client_morning', label: 'Client Reminder — Morning' },
  { key: 'client_evening', label: 'Client Reminder — Evening' },
  { key: 'employee_sod',   label: 'Employee Start-of-Day' },
  { key: 'employee_eod',   label: 'Employee End-of-Day' },
];

function ScheduleEditorPanel() {
  const [sched, setSched] = React.useState(null);
  const [draft, setDraft] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    adminApi.get('/api/admin/whatsapp/schedule').then(r => {
      setSched(r.data.schedule); setDraft(r.data.schedule);
    }).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const r = await adminApi.put('/api/admin/whatsapp/schedule', draft);
      setSched(r.data.schedule); toast.success('Schedule updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const setField = (key, part, val) =>
    setDraft(d => ({ ...d, [key]: { ...d[key], [part]: parseInt(val || 0, 10) } }));

  return (
    <div className="soft-card overflow-hidden" data-testid="wa-schedule-panel">
      <div className="px-5 py-3 border-b border-white/30">
        <h2 className="text-sm font-semibold text-[#1a1a2e]">WhatsApp Schedule (IST)</h2>
        <p className="text-xs text-neutral-500">Change any cron time. Applies live — no redeploy.</p>
      </div>
      {sched && (
        <div className="p-4 space-y-2">
          {_SCHEDULE_FIELDS.map(f => (
            <div key={f.key} className="flex items-center justify-between gap-3">
              <p className="text-sm text-neutral-700 min-w-[220px]">{f.label}</p>
              <div className="flex items-center gap-1">
                <input type="number" min="0" max="23" value={draft[f.key]?.hour ?? 0}
                  onChange={e => setField(f.key, 'hour', e.target.value)}
                  className="w-14 px-2 py-1 border border-white/50 rounded text-sm text-right bg-white/70"
                  data-testid={`wa-sched-${f.key}-hour`} />
                <span className="text-neutral-400">:</span>
                <input type="number" min="0" max="59" value={draft[f.key]?.minute ?? 0}
                  onChange={e => setField(f.key, 'minute', e.target.value)}
                  className="w-14 px-2 py-1 border border-white/50 rounded text-sm text-right bg-white/70"
                  data-testid={`wa-sched-${f.key}-minute`} />
                <span className="text-xs text-neutral-500 ml-1">IST</span>
              </div>
            </div>
          ))}
          <div className="pt-2 flex justify-end">
            <button onClick={save} disabled={saving}
              className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
              data-testid="wa-sched-save">
              {saving ? 'Saving…' : 'Save Schedule'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Send Log Panel ───────────────────────────────────────────────────────
function SendLogPanel() {
  const [rows, setRows] = React.useState([]);
  const [filters, setFilters] = React.useState({ kind: '', status: '', to: '' });
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.kind)   p.set('kind', filters.kind);
      if (filters.status) p.set('status', filters.status);
      if (filters.to)     p.set('to', filters.to);
      p.set('limit', '100');
      const r = await adminApi.get(`/api/admin/whatsapp/sends?${p}`);
      setRows(r.data.sends || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [filters]);
  React.useEffect(() => { load(); }, [load]);

  const retry = async (id) => {
    try {
      await adminApi.post(`/api/admin/whatsapp/sends/${id}/retry`);
      toast.success('Re-fired'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Retry failed'); }
  };

  return (
    <div className="soft-card overflow-hidden" data-testid="wa-sendlog-panel">
      <div className="px-5 py-3 border-b border-white/30 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold text-[#1a1a2e]">WhatsApp Send Log</h2>
          <p className="text-xs text-neutral-500">Every send, live delivery status, and retry on failed rows.</p>
        </div>
        <button onClick={load} disabled={loading}
          className="px-2 py-1 text-xs rounded-lg border border-white/50 bg-white/70 hover:bg-white"
          data-testid="wa-sendlog-refresh">
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      <div className="p-4 flex items-center gap-2 flex-wrap">
        <input value={filters.kind} onChange={e => setFilters(f => ({ ...f, kind: e.target.value }))}
          placeholder="kind (e.g. invoice)" className="px-2 py-1 border border-white/50 rounded text-xs w-40 bg-white/70"
          data-testid="wa-sendlog-kind" />
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
          className="px-2 py-1 border border-white/50 rounded text-xs bg-white/70"
          data-testid="wa-sendlog-status">
          <option value="">any status</option>
          <option value="sent">sent</option>
          <option value="failed">failed</option>
          <option value="blocked">blocked</option>
        </select>
        <input value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
          placeholder="to (phone contains…)" className="px-2 py-1 border border-white/50 rounded text-xs w-48 bg-white/70"
          data-testid="wa-sendlog-to" />
      </div>
      <div className="overflow-x-auto max-h-[60vh]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white/95 backdrop-blur">
            <tr className="text-left text-neutral-500 border-b border-white/40">
              <th className="px-3 py-2">Sent at</th>
              <th className="px-3 py-2">Kind</th>
              <th className="px-3 py-2">To</th>
              <th className="px-3 py-2">Template</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Delivery</th>
              <th className="px-3 py-2">Error</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-neutral-400">No sends match your filters.</td></tr>
            ) : rows.map(r => (
              <tr key={r._id} className="border-b border-white/20 hover:bg-blue-50/30" data-testid={`wa-sendlog-row-${r._id}`}>
                <td className="px-3 py-1.5 text-neutral-500 whitespace-nowrap">{(r.sent_at || r.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                <td className="px-3 py-1.5">{r.kind}</td>
                <td className="px-3 py-1.5 font-mono">{r.to}</td>
                <td className="px-3 py-1.5 text-neutral-500">{r.template}</td>
                <td className="px-3 py-1.5">
                  <span className={
                    r.status === 'sent'    ? 'text-emerald-700' :
                    r.status === 'failed'  ? 'text-red-700' :
                    r.status === 'blocked' ? 'text-amber-700' :
                    'text-neutral-500'
                  }>{r.status}</span>
                </td>
                <td className="px-3 py-1.5 text-neutral-600">{r.delivery_status || '—'}</td>
                <td className="px-3 py-1.5 text-red-600 max-w-[220px] truncate" title={r.error || ''}>{r.error || ''}</td>
                <td className="px-3 py-1.5">
                  {(r.status === 'failed' || r.status === 'blocked') && (
                    <button onClick={() => retry(r._id)}
                      className="text-blue-600 hover:underline"
                      data-testid={`wa-sendlog-retry-${r._id}`}>Retry</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Email Master Control + Preview ────────────────────────────────────
// Mirrors the WhatsApp panel, but drives GET/PUT /api/admin/email-digests/toggles
// and adds a Preview modal that renders the exact HTML each digest would ship.
const EMAIL_FLOWS = [
  { key: 'invoice_sends',    label: 'Invoice sends',                     desc: 'Admin clicking "Send Invoice by Email" on any invoice', previewKind: null },
  { key: 'admin_digest',     label: 'Admin daily digest (9 AM)',         desc: 'Ops snapshot emailed to every admin/accountant/HR with an email', previewKind: 'admin_digest' },
  { key: 'client_reminders', label: 'Client reminders (10 AM / 6 PM)',   desc: 'Internal nudge emailed to each client\'s account-manager POC — never to the client', previewKind: 'client_reminder_morning' },
  { key: 'employee_sod',     label: 'Employee SOD (10 AM)',              desc: 'Today\'s + tomorrow\'s tasks emailed to each employee', previewKind: 'employee_sod' },
  { key: 'employee_eod',     label: 'Employee EOD (8 PM)',               desc: 'Completed today + overdue emailed to each employee', previewKind: 'employee_eod' },
];

function EmailControlPanel() {
  const [toggles, setToggles] = React.useState(null);
  const [status, setStatus] = React.useState(null);
  const [saving, setSaving] = React.useState(null);
  const [firing, setFiring] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [previewKind, setPreviewKind] = React.useState(null);
  const [previewData, setPreviewData] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);

  const load = async () => {
    try {
      const r = await adminApi.get('/api/admin/email-digests/status');
      setToggles(r.data.toggles);
      setStatus({ configured: r.data.configured, sender: r.data.sender, adminRecipients: r.data.admin_recipients });
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load email digest status');
    }
  };
  React.useEffect(() => { load(); }, []);

  const setFlow = async (key, value) => {
    setSaving(key);
    try {
      const r = await adminApi.put('/api/admin/email-digests/toggles', { [key]: value });
      setToggles(r.data.toggles);
      toast.success(`Email · ${key.replace(/_/g, ' ')} ${value ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save toggle');
    } finally { setSaving(null); }
  };

  const allOn  = toggles && EMAIL_FLOWS.every(f => toggles[f.key]);
  const allOff = toggles && EMAIL_FLOWS.every(f => !toggles[f.key]);
  const bulkSet = async (value) => {
    if (!window.confirm(`${value ? 'Turn ON' : 'Turn OFF'} EVERY automated email flow?`)) return;
    setSaving('__all__');
    try {
      const patch = Object.fromEntries(EMAIL_FLOWS.map(f => [f.key, value]));
      const r = await adminApi.put('/api/admin/email-digests/toggles', patch);
      setToggles(r.data.toggles);
      toast.success(`All email flows ${value ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk toggle failed');
    } finally { setSaving(null); }
  };

  const openPreview = async (kind) => {
    if (!kind) return;
    setPreviewKind(kind);
    setPreviewData(null);
    setPreviewLoading(true);
    try {
      const r = await adminApi.post(`/api/admin/email-digests/preview?kind=${encodeURIComponent(kind)}`);
      setPreviewData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
      setPreviewKind(null);
    } finally { setPreviewLoading(false); }
  };

  const runNow = async (flowKey) => {
    if (!window.confirm(`Fire the '${flowKey}' email digest NOW to real recipients? (Idempotency lock still applies per-day.)`)) return;
    setFiring(flowKey);
    try {
      let r;
      if (flowKey === 'admin_digest') r = await adminApi.post('/api/admin/email-digests/admin-digest/run-now');
      else if (flowKey === 'client_reminders') r = await adminApi.post('/api/admin/email-digests/client-reminders/run-now', { slot: 'Morning' });
      else if (flowKey === 'employee_sod')     r = await adminApi.post('/api/admin/email-digests/employee-digest/run-now?when=start');
      else if (flowKey === 'employee_eod')     r = await adminApi.post('/api/admin/email-digests/employee-digest/run-now?when=end');
      else return;
      toast.success(`Fired · ${JSON.stringify(r.data).slice(0, 200)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed');
    } finally { setFiring(null); }
  };

  return (
    <div className="soft-card overflow-hidden" data-testid="email-control-panel">
      <div className="px-5 py-3 border-b border-white/30 bg-white/40 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold text-[#1a1a2e]">Email Master Control</h2>
          <p className="text-xs text-neutral-500">
            Well-branded HTML mirrors of every WhatsApp automation.
            {status && (
              <>
                {' '}Sender <strong className="text-neutral-700">{status.sender}</strong> —
                <span className={status.configured ? 'text-emerald-700' : 'text-red-700'}>
                  {' '}{status.configured ? 'connected' : 'NOT connected (connect via Settings → Email)'}
                </span>
                {' '}· <strong>{status.adminRecipients}</strong> admin recipient(s)
              </>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => bulkSet(false)} disabled={!toggles || allOff || saving}
            className="px-3 py-1.5 text-xs rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
            data-testid="email-panic-off">
            🛑 Turn all OFF
          </button>
          <button onClick={() => bulkSet(true)} disabled={!toggles || allOn || saving}
            className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
            data-testid="email-panic-on">
            Turn all ON
          </button>
        </div>
      </div>
      {error && <div className="p-4 text-sm text-red-700">{error}</div>}
      {toggles && (
        <div className="divide-y divide-white/30">
          {EMAIL_FLOWS.map(f => (
            <div key={f.key} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-white/40" data-testid={`email-toggle-row-${f.key}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#1a1a2e]">{f.label}</p>
                <p className="text-xs text-neutral-500">{f.desc}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {f.previewKind && (
                  <button onClick={() => openPreview(f.previewKind)}
                    className="px-2.5 py-1 text-[11px] rounded-md bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                    data-testid={`email-preview-${f.key}`}>
                    Preview
                  </button>
                )}
                {f.previewKind && (
                  <button onClick={() => runNow(f.key)}
                    disabled={firing === f.key || !toggles[f.key] || !status?.configured}
                    className="px-2.5 py-1 text-[11px] rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
                    data-testid={`email-run-${f.key}`}>
                    {firing === f.key ? 'Sending…' : 'Run now'}
                  </button>
                )}
                <button
                  onClick={() => setFlow(f.key, !toggles[f.key])}
                  disabled={saving === f.key || saving === '__all__'}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    toggles[f.key] ? 'bg-emerald-500' : 'bg-neutral-300'
                  } ${saving === f.key ? 'opacity-50' : ''}`}
                  data-testid={`email-toggle-${f.key}`}
                  aria-checked={!!toggles[f.key]}
                >
                  <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                    toggles[f.key] ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!toggles && !error && <div className="p-4 text-sm text-neutral-500">Loading…</div>}

      {/* Preview modal */}
      {previewKind && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="email-preview-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <div>
                <div className="text-xs text-neutral-500">Preview</div>
                <div className="text-sm font-semibold text-neutral-800">
                  {previewData?.subject || previewKind}
                </div>
              </div>
              <button onClick={() => { setPreviewKind(null); setPreviewData(null); }}
                className="text-neutral-500 hover:text-neutral-800" data-testid="email-preview-close">✕</button>
            </div>
            <div className="flex-1 overflow-auto bg-neutral-100">
              {previewLoading && <div className="p-8 text-center text-sm text-neutral-500">Rendering with real data…</div>}
              {previewData && (
                <iframe title="email-preview" srcDoc={previewData.html}
                  className="w-full h-[70vh] border-0 bg-white" data-testid="email-preview-iframe" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

